import React from "react";
import { Typography, Paper, Box } from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  Comment as CommentIcon,
  ChangeCircle as ChangeCircleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { ETicketAction } from "../../Utils/Enums";

interface TicketHistoryTimelineProps {
  history: Array<{
    action: string;
    performedBy?: { firstName?: string; lastName?: string; };
    previousValue?: string;
    newValue?: string;
    comment?: string;
    timestamp: string | Date;
  }>;
}

const TicketHistoryTimeline: React.FC<TicketHistoryTimelineProps> = ({ history }) => {
  const getActionIcon = (action: string) => {
    switch (action) {
      case ETicketAction.CREATED:
        return <AddIcon />;
      case ETicketAction.UPDATED:
        return <EditIcon />;
      case ETicketAction.ASSIGNED:
        return <PersonAddIcon />;
      case ETicketAction.COMMENTED:
        return <CommentIcon />;
      case ETicketAction.STATUS_CHANGED:
        return <ChangeCircleIcon />;
      case ETicketAction.RESOLVED:
        return <CheckCircleIcon />;
      case ETicketAction.CLOSED:
        return <CancelIcon />;
      default:
        return <EditIcon />;
    }
  };

  const getActionColor = (action: string): "primary" | "secondary" | "success" | "error" | "warning" | "info" | "grey" => {
    switch (action) {
      case ETicketAction.CREATED:
        return "primary";
      case ETicketAction.ASSIGNED:
        return "info";
      case ETicketAction.COMMENTED:
        return "secondary";
      case ETicketAction.STATUS_CHANGED:
        return "warning";
      case ETicketAction.RESOLVED:
        return "success";
      case ETicketAction.CLOSED:
        return "grey";
      default:
        return "primary";
    }
  };

  const getActionLabel = (action: string): string => {
    switch (action) {
      case ETicketAction.CREATED:
        return Vocabulary.actionCreated;
      case ETicketAction.UPDATED:
        return Vocabulary.actionUpdated;
      case ETicketAction.ASSIGNED:
        return Vocabulary.actionAssigned;
      case ETicketAction.COMMENTED:
        return Vocabulary.actionCommented;
      case ETicketAction.STATUS_CHANGED:
        return Vocabulary.actionStatusChanged;
      case ETicketAction.RESOLVED:
        return Vocabulary.actionResolved;
      case ETicketAction.CLOSED:
        return Vocabulary.actionClosed;
      default:
        return action;
    }
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleString("ro-RO", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!history || history.length === 0) {
    return (
      <Typography color="textSecondary" align="center" sx={{ p: 2 }}>
        {Vocabulary.noData}
      </Typography>
    );
  }

  return (
    <Box sx={{ position: "relative", pl: 4 }}>
      {history.map((item, index) => (
        <Box key={`history-${index}`} sx={{ position: "relative", pb: 3 }}>
          {/* Timeline Line */}
          {index < history.length - 1 && (
            <Box
              sx={{
                position: "absolute",
                left: 15,
                top: 40,
                bottom: 0,
                width: 2,
                backgroundColor: "#e0e0e0",
              }}
            />
          )}

          {/* Timeline Dot with Icon */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 8,
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor:
                getActionColor(item.action) === "primary" ? "#1976d2" :
                  getActionColor(item.action) === "success" ? "#2e7d32" :
                    getActionColor(item.action) === "warning" ? "#ed6c02" :
                      getActionColor(item.action) === "error" ? "#d32f2f" :
                        getActionColor(item.action) === "info" ? "#0288d1" :
                          getActionColor(item.action) === "secondary" ? "#9c27b0" : "#757575",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 16,
            }}
          >
            {getActionIcon(item.action)}
          </Box>

          {/* Content */}
          <Box sx={{ ml: 4 }}>
            <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 0.5 }}>
              {formatDate(item.timestamp)}
            </Typography>

            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                {getActionLabel(item.action)}
              </Typography>

              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                {Vocabulary.by}: {item.performedBy?.firstName} {item.performedBy?.firstName}
              </Typography>

              {item.comment && (
                <Box sx={{ mt: 1, p: 1, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {item.comment}
                  </Typography>
                </Box>
              )}

              {item.previousValue && item.newValue && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="textSecondary">
                    {Vocabulary[item.previousValue] || item.previousValue} → {Vocabulary[item.newValue] || item.newValue}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default TicketHistoryTimeline;
